import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { useChatHistory } from '../hooks/useChatHistory';
import AudioPlayerWithSpeed from './AudioPlayerWithSpeed';

// --- Helpers sempre no topo! ---
function getSeconds(obj) {
  if (!obj) return 0;
  if (Object.prototype.hasOwnProperty.call(obj, 'seconds')) return obj.seconds;
  const secondsKey = Object.keys(obj).find((k) => k.endsWith('seconds'));
  return secondsKey ? obj[secondsKey] : 0;
}
function getNanos(obj) {
  if (!obj) return 0;
  if (Object.prototype.hasOwnProperty.call(obj, 'nanos')) return obj.nanos;
  if (Object.prototype.hasOwnProperty.call(obj, 'nanoseconds'))
    return obj.nanoseconds;
  const nanosKey = Object.keys(obj).find((k) => k.endsWith('nanos'));
  if (nanosKey) return obj[nanosKey];
  const nanosecondsKey = Object.keys(obj).find((k) => k.endsWith('nanoseconds'));
  return nanosecondsKey ? obj[nanosecondsKey] : 0;
}
function toMs(t) {
  if (!t) return 0;
  const seconds = getSeconds(t);
  const nanos = getNanos(t);
  return seconds * 1000 + nanos / 1e6;
}
function fmtDateOnly(ts) {
  const d = new Date(toMs(ts));
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}
function fmtHoraSm(ts) {
  const d = new Date(toMs(ts));
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
function getMessageKey(msg) {
  const ts = msg.timestamp || {};
  const s = getSeconds(ts);
  const n = getNanos(ts);
  return `${s}-${n}`;
}
// Util para pegar primeiro e Ãºltimo nome (separando por espaÃ§o)
function extrairPrimeiroUltimoNome(nome) {
  if (!nome) return '';
  const partes = nome.trim().split(' ').filter(Boolean);
  if (partes.length === 0) return '';
  if (partes.length === 1) return partes[0];
  return `${partes[0]} ${partes[partes.length - 1]}`;
}
// Filtro WhatsApp style
function filtrarConversas(arr, busca) {
  if (!busca.trim()) return arr;
  const buscaNorm = busca.trim().toLowerCase();
  return arr.filter((conv) => {
    const nome = (conv.atendenteNome ?? '').toLowerCase();
    const diaConv = fmtDateOnly(conv.dataFim);
    return nome.includes(buscaNorm) || diaConv.includes(buscaNorm);
  });
}
// --- Fim Helpers ---

const CLIENTE_BG = '#00678D';
const SUPORTE_BG = '#525661ff';
const CLIENTE_COLOR = '#ffffff';
const SUPORTE_COLOR = '#e0e0e0';
const AVATAR_CLIENTE_BG = '#00678D';
const AVATAR_SUPORTE_BG = '#525661ff';
const DIVIDER_SX = { borderColor: 'rgba(155,155,255,0.19)', opacity: 1 };

function MessageBubble({ msg, nomeCliente, nomeSuporte }) {
  const isCliente = msg.remetente === 'cliente';
  const bubbleBg = isCliente ? CLIENTE_BG : SUPORTE_BG;
  const txtColor = isCliente ? CLIENTE_COLOR : SUPORTE_COLOR;
  const avatarBg = isCliente ? AVATAR_CLIENTE_BG : AVATAR_SUPORTE_BG;
  const nomeRemetente = isCliente ? nomeCliente : nomeSuporte;
  const fallbackLetter = isCliente ? 'C' : 'S';
  const avatarLetter =
    nomeRemetente && nomeRemetente.length > 0
      ? nomeRemetente[0].toUpperCase()
      : fallbackLetter;

  const renderContent = () => {
    if (msg.tipo === 'midia') {
      const url = msg.conteudo || '';
      const subtype = (msg.subtipo || '').toLowerCase();

      if (subtype === 'mp4') {
        return (
          <Box sx={{ width: '100%', maxWidth: 400, mb: 1 }}>
            <video
              src={url}
              controls
              style={{
                width: '100%',
                borderRadius: 10,
                background: '#10101a',
                boxShadow: '0 1px 8px rgba(0,0,0,0.22)',
              }}
            >
              <track kind="captions" src="" label="Sem legendas" />
            </video>
          </Box>
        );
      }
      if (subtype === 'pdf') {
        return (
          <Box sx={{ width: '100%', maxWidth: 400, height: 320, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <PdfIcon sx={{ mr: 1, color: txtColor }} />
            </Box>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'block', height: '100%' }}
              aria-label="Abrir PDF em nova aba"
            >
              <iframe
                src={url}
                title="Arquivo PDF"
                width="100%"
                height="100%"
                style={{
                  border: '1px solid #2228',
                  borderRadius: 6,
                  background: '#ededed',
                }}
              />
            </a>
          </Box>
        );
      }
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(subtype)) {
        return (
          <Box sx={{ width: '100%', maxWidth: 160 }}>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <img
                src={url}
                alt="imagem"
                style={{
                  width: '100%',
                  borderRadius: 8,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  display: 'block',
                  background: '#15151c',
                  border: '1px solid #1a2643',
                }}
              />
            </a>
          </Box>
        );
      }
      if (['webm', 'ogg', 'mp3', 'wav', 'm4a', 'aac'].includes(subtype)) {
        return (
          <Box
            sx={{
              minWidth: 320,
              maxWidth: 520,
              width: '100%',
              p: 1.2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isCliente
                ? 'rgba(255,255,255,0.35)'
                : 'rgba(255,255,255,0.24)',
              background: isCliente
                ? 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.12) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)',
              boxShadow: '0 6px 14px rgba(0,0,0,0.16)',
              color: txtColor,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <GraphicEqIcon fontSize="small" sx={{ color: 'inherit' }} />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: 'inherit' }}
              >
                Mensagem de voz
              </Typography>
            </Box>
            <AudioPlayerWithSpeed
              src={url}
              tone={isCliente ? 'outgoing' : 'incoming'}
            />
          </Box>
        );
      }
      return (
        <Box>
          <Button
            size="small"
            variant="outlined"
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ml: 1,
              color: txtColor,
              borderColor: txtColor,
              fontSize: '0.75rem',
              py: 0,
              px: 1,
            }}
          >
            Baixar/abrir
          </Button>
        </Box>
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: txtColor,
        }}
      >
        {msg.conteudo}
      </Typography>
    );
  };

  return (
    <Stack
      direction={isCliente ? 'row-reverse' : 'row'}
      spacing={1.5}
      alignItems="flex-end"
      sx={{ mb: 2 }}
    >
      <Avatar
        sx={{
          bgcolor: avatarBg,
          color: txtColor,
          width: 40,
          height: 40,
          fontWeight: 600,
          fontSize: '1rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
      >
        {avatarLetter}
      </Avatar>
      <Box
        sx={{
          maxWidth: '70%',
          px: 2,
          py: 1.25,
          borderRadius: isCliente ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          bgcolor: bubbleBg,
          color: txtColor,
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}
      >
        {renderContent()}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.75,
            textAlign: 'right',
            opacity: 0.7,
            color: txtColor,
            fontSize: '0.7rem',
          }}
        >
          {fmtHoraSm(msg.timestamp)}
        </Typography>
      </Box>
    </Stack>
  );
}

function ChatHistoryDialog({ open, onClose, clienteId, clienteTitle }) {
  const [busca, setBusca] = useState('');

  const {
    loadingHistorico,
    listaConversas,
    conversaAberta,
    fetchHistorico,
    abrirConversaEspecifica,
  } = useChatHistory(clienteId, open);

  useEffect(() => {
    fetchHistorico();
  }, [fetchHistorico]);

  const nomeCliente = conversaAberta?.nomeCliente ?? 'Cliente';
  const nomeSuporte = conversaAberta?.atendenteNome ?? 'Suporte';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      {/* TÃTULO E BOTÃƒO FECHAR */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 2,
          pb: 0,
        }}
      >
        <Typography variant="h6" component="div">
          Histórico de {clienteTitle}
        </Typography>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            minWidth: 36,
            minHeight: 36,
            borderRadius: '10px',
            ml: 2,
            fontWeight: 700,
            mb: 1,
            backgroundColor: '#00678D',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#00678D',
              boxShadow: 'none',
            },
            textTransform: 'none',
          }}
        >
          Fechar
        </Button>
      </DialogTitle>
      <Divider sx={DIVIDER_SX} />

      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: 520, height: '70vh' }}>
          {/* Lista de conversas */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: { md: '1px solid rgba(0,0,0,0.10)' },
              display: 'flex',
              flexDirection: 'column',
              height: '70vh',
              maxHeight: '70vh',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                px: 0,
                pt: 2,
                pb: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  pl: 2,
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: 1,
                  color: '#fff',
                }}
              >
                Conversas
              </Typography>
              {/* CAMPO DE BUSCA DA LISTA */}
              <Box sx={{ px: 2, pb: 1, pt: 1 }}>
                <input
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 12,
                    border: '2px solid #e0e0e070',
                    outline: 'none',
                    fontSize: 16,
                    background: 'rgba(78, 77, 77, 0.48)',
                    color: '#fff',
                  }}
                  type="text"
                  placeholder="Buscar por atendente ou data (10/09/2025)"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </Box>
              <Divider sx={DIVIDER_SX} />
              <List
                dense
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pr: 1,
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#00678da6 #23272E',
                  '&::-webkit-scrollbar': { width: 8 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#00678da6',
                    borderRadius: 8,
                  },
                  '&::-webkit-scrollbar-track': { background: '#00678da6' },
                  background: 'transparent',
                }}
              >
                {loadingHistorico ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  filtrarConversas(listaConversas, busca).map((conv, idx) => {
                    const ativa =
                      conversaAberta?.atendimentoId === conv.atendimentoId;
                    return (
                      <React.Fragment key={conv.atendimentoId}>
                        <ListItem
                          alignItems="flex-start"
                          selected={ativa}
                          onClick={() => abrirConversaEspecifica(conv)}
                          button
                          sx={{
                            borderRadius: 0,
                            bgcolor: ativa ? '#00678da6' : 'transparent',
                            '&.Mui-selected': {
                              bgcolor: '#00678da6 !important',
                            },
                            '&:hover, &.Mui-selected:hover': {
                              bgcolor: '#00678da6 !important',
                            },
                            px: 2,
                            py: 1.1,
                            minHeight: 60,
                            height: 70,
                            transition: 'background 0.18s, border-radius 0.18s',
                            boxShadow: ativa ? 2 : undefined,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                              sx={{
                                fontWeight: 700,
                                fontSize: 19,
                                width: 40,
                                height: 40,
                                bgcolor: '#23272E',
                                color: '#fff',
                                mx: 1.5,
                              }}
                            >
                              {conv.atendenteNome && conv.atendenteNome.length > 0
                                ? conv.atendenteNome[0].toUpperCase()
                                : 'S'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 15,
                                  color: '#fff',
                                }}
                              >
                                Atendente:{' '}
                                {extrairPrimeiroUltimoNome(
                                  conv.atendenteNome ?? 'Desconhecido'
                                )}
                              </Typography>
                            }
                            secondary={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                  }}
                                >
                                  {fmtDateOnly(conv.dataFim)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={conv.duracaoFormatada || ''}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 11,
                                    ml: 1,
                                    height: 20,
                                    px: 1.3,
                                    bgcolor: '#23272E',
                                    color: '#fff',
                                    borderRadius: '6px',
                                  }}
                                />
                              </Stack>
                            }
                            disableTypography
                          />
                        </ListItem>
                        {idx < listaConversas.length - 1 && (
                          <Divider sx={{ my: 0.5, ...DIVIDER_SX }} />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
                {!loadingHistorico &&
                  filtrarConversas(listaConversas, busca).length === 0 && (
                    <Box
                      sx={{
                        py: 8,
                        px: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#bdbdbd',
                        textAlign: 'center',
                        opacity: 0.95,
                      }}
                    >
                      <ChatBubbleOutlineIcon
                        sx={{ mb: 1, fontSize: 46, color: '#95a2bb' }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 0.5, color: '#b5c1d8' }}
                      >
                        Nenhum histÃ³rico encontrado
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#bdbdbd', maxWidth: 180 }}
                      >
                        Este usuÃ¡rio ainda nÃ£o tem conversas salvas
                      </Typography>
                    </Box>
                  )}
              </List>
            </Box>
          </Grid>
          {/* Mensagens */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '70vh',
              maxHeight: '70vh',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {conversaAberta
                    ? `${fmtDateOnly(conversaAberta.dataFim)}`
                    : 'Selecione uma conversa'}
                </Typography>
                {conversaAberta && (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Encerrado: {fmtDateOnly(conversaAberta.dataFim)}{' '}
                    {fmtHoraSm(conversaAberta.dataFim)}
                  </Typography>
                )}
              </Stack>
              <Divider sx={DIVIDER_SX} />
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  py: 2,
                  px: 1,
                  minHeight: 0,
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#00678da6 #23272E',
                  '&::-webkit-scrollbar': { width: 8 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#00678da6',
                    borderRadius: 8,
                  },
                  '&::-webkit-scrollbar-track': { background: '#23272E' },
                  background: 'transparent',
                }}
              >
                {conversaAberta?.mensagens?.map((m) => (
                  <MessageBubble
                    key={getMessageKey(m)}
                    msg={m}
                    nomeCliente={nomeCliente}
                    nomeSuporte={nomeSuporte}
                  />
                ))}
                {/* AVISO MINIMALISTA "SELECIONE UMA CONVERSA" */}
                {!conversaAberta && (
                  <Box
                    sx={{
                      py: 6,
                      px: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#bdbdbd',
                      textAlign: 'center',
                      opacity: 0.95,
                    }}
                  >
                    <ChatBubbleOutlineIcon
                      sx={{ mb: 1, fontSize: 44, color: '#95a2bb' }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 500, color: '#b5c1d8' }}
                    >
                      Selecione uma conversa
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={DIVIDER_SX} />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default ChatHistoryDialog;
